import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabase';
import {
  loadTransactionsForMonth, loadBudgetCategories,
  logTransaction, deleteTransaction, upsertBudget, deleteBudget,
  getMonthlyTotals, getBudgetProgress, generateMoneyInsight,
  DEFAULT_CATEGORIES,
  formatGBP, formatMonthLabel, formatTransactionDate,
  todayLocalDateStr, getCurrentMonth, getPreviousMonth, getNextMonth,
} from '../moneyData';
import Card from '../../design-system/Card';
import MetricCard from '../../components/MetricCard';
import EmptyState from '../../components/EmptyState';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';
import TransactionRow from '../../components/TransactionRow';
import { colors, spacing, radius, fontStack } from '../../design-system/tokens';

/**
 * /system/money — Phase 4D rebuild.
 *
 * Replaces the legacy 1100-line dark UI. Same data layer (moneyData.js),
 * same DB shape, same `generateMoneyInsight` Sonnet call.
 *
 * View states:
 *   home → log → budgets → history → insight
 * The OLD `gen-error` view is folded into ErrorState.
 */
export default function Money() {
  const [userId, setUserId] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(() => getCurrentMonth());
  const [transactions, setTransactions] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [prevMonthTotals, setPrevMonthTotals] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [view, setView] = useState('home'); // home | log | budgets | history | insight
  const [insightResult, setInsightResult] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState(null);

  const [toast, setToast] = useState(null);

  /* MOUNT */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data: authData } = await supabase.auth.getUser();
        const user = authData?.user;
        if (cancelled) return;
        if (!user) { setLoadError('Not signed in'); return; }
        setUserId(user.id);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Could not load Money');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* Refetch when month or user changes */
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const prev = getPreviousMonth(selectedMonth.year, selectedMonth.month);
        const [tx, bg, prevTx] = await Promise.all([
          loadTransactionsForMonth(userId, selectedMonth.year, selectedMonth.month).catch(() => []),
          loadBudgetCategories(userId).catch(() => []),
          loadTransactionsForMonth(userId, prev.year, prev.month).catch(() => []),
        ]);
        if (cancelled) return;
        setTransactions(tx);
        setBudgets(bg);
        setPrevMonthTotals(getMonthlyTotals(prevTx));
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Could not load Money');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, selectedMonth.year, selectedMonth.month]);

  const refreshAll = async () => {
    if (!userId) return;
    const [tx, bg] = await Promise.all([
      loadTransactionsForMonth(userId, selectedMonth.year, selectedMonth.month).catch(() => []),
      loadBudgetCategories(userId).catch(() => []),
    ]);
    setTransactions(tx);
    setBudgets(bg);
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 1800);
  };

  /* HANDLERS */

  const handleLog = async (data) => {
    try {
      await logTransaction({ user_id: userId, ...data });
      await refreshAll();
      setView('home');
      showToast(data.log_type === 'income' ? 'Income logged' : 'Expense logged');
    } catch (err) {
      showToast(err.message || 'Could not save', 'error');
    }
  };

  const handleDeleteTransaction = async (tx) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await deleteTransaction(tx.id);
      await refreshAll();
      showToast('Deleted');
    } catch (err) {
      showToast(err.message || 'Delete failed', 'error');
    }
  };

  const handleSaveBudget = async ({ category, monthly_limit }) => {
    try {
      await upsertBudget({ user_id: userId, category, monthly_limit });
      await refreshAll();
      showToast('Budget saved');
    } catch (err) {
      showToast(err.message || 'Save failed', 'error');
    }
  };

  const handleClearBudget = async (category) => {
    try {
      await deleteBudget(userId, category);
      await refreshAll();
      showToast('Budget removed');
    } catch (err) {
      showToast(err.message || 'Remove failed', 'error');
    }
  };

  const handleGenerateInsight = async () => {
    setView('insight');
    setInsightResult(null);
    setInsightError(null);
    setInsightLoading(true);
    try {
      const result = await generateMoneyInsight({
        transactions,
        budgets,
        currentMonth: formatMonthLabel(selectedMonth.year, selectedMonth.month),
      });
      setInsightResult(result);
    } catch (err) {
      setInsightError(err.message || 'Could not generate insight');
    } finally {
      setInsightLoading(false);
    }
  };

  const totals = useMemo(() => getMonthlyTotals(transactions), [transactions]);
  const budgetProgress = useMemo(
    () => getBudgetProgress(transactions, budgets),
    [transactions, budgets]
  );

  /* RENDER */

  if (loadError && !userId) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <ErrorState title="Couldn't load Money" message={loadError} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', fontFamily: fontStack }}>
      <Header
        selectedMonth={selectedMonth}
        onPrev={() => setSelectedMonth(getPreviousMonth(selectedMonth.year, selectedMonth.month))}
        onNext={() => setSelectedMonth(getNextMonth(selectedMonth.year, selectedMonth.month))}
        view={view}
        onBackToHome={() => setView('home')}
      />

      {/* View switcher */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg, marginTop: spacing.lg }}>
        {view === 'home' && (
          <HomeView
            loading={loading}
            error={loadError}
            totals={totals}
            prevTotals={prevMonthTotals}
            transactions={transactions}
            budgets={budgets}
            budgetProgress={budgetProgress}
            onLog={() => setView('log')}
            onBudgets={() => setView('budgets')}
            onHistory={() => setView('history')}
            onInsight={handleGenerateInsight}
            onDeleteTransaction={handleDeleteTransaction}
            onRetry={refreshAll}
          />
        )}
        {view === 'log' && (
          <LogTransactionView
            onSave={handleLog}
            onCancel={() => setView('home')}
          />
        )}
        {view === 'budgets' && (
          <BudgetsView
            budgets={budgets}
            onSave={handleSaveBudget}
            onClear={handleClearBudget}
          />
        )}
        {view === 'history' && (
          <HistoryView
            userId={userId}
            onDelete={handleDeleteTransaction}
          />
        )}
        {view === 'insight' && (
          <InsightView
            loading={insightLoading}
            error={insightError}
            result={insightResult}
            monthLabel={formatMonthLabel(selectedMonth.year, selectedMonth.month)}
            onRetry={handleGenerateInsight}
          />
        )}
      </div>

      {toast && <Toast {...toast} />}
    </div>
  );
}

/* ================================================================
   Header (back arrow / title / month selector)
   ================================================================ */

function Header({ selectedMonth, onPrev, onNext, view, onBackToHome }) {
  const monthLabel = formatMonthLabel(selectedMonth.year, selectedMonth.month);
  const inSubView = view !== 'home';

  return (
    <header style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <a
          href="/system"
          onClick={(e) => { if (inSubView) { e.preventDefault(); onBackToHome(); } }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color: colors.textSecondary,
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          ← {inSubView ? 'Back to Money' : 'System'}
        </a>
        <h1
          style={{
            margin: 0,
            fontSize: 24,
            fontWeight: 600,
            color: colors.textPrimary,
            letterSpacing: '-0.01em',
          }}
        >
          {viewTitle(view)}
        </h1>
        {!inSubView && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            <ArrowBtn label="Previous month" onClick={onPrev} dir="prev" />
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: colors.textPrimary,
                minWidth: 110,
                textAlign: 'center',
              }}
            >
              {monthLabel}
            </div>
            <ArrowBtn label="Next month" onClick={onNext} dir="next" />
          </div>
        )}
      </div>
      {!inSubView && (
        <p style={{ margin: 0, color: colors.textSecondary, fontSize: 14 }}>
          A calm look at the money this month.
        </p>
      )}
    </header>
  );
}

function viewTitle(v) {
  return {
    home: 'Money',
    log: 'Log a transaction',
    budgets: 'Budgets',
    history: 'History',
    insight: 'Money Insight',
  }[v] || 'Money';
}

function ArrowBtn({ label, onClick, dir }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      style={{
        width: 32,
        height: 32,
        padding: 0,
        border: `1px solid ${colors.border}`,
        background: '#fff',
        borderRadius: radius.sm,
        cursor: 'pointer',
        color: colors.textSecondary,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {dir === 'prev' ? <path d="M15 18l-6-6 6-6" /> : <path d="M9 18l6-6-6-6" />}
      </svg>
    </button>
  );
}

/* ================================================================
   Home view
   ================================================================ */

function HomeView({
  loading, error, totals, prevTotals, transactions, budgets, budgetProgress,
  onLog, onBudgets, onHistory, onInsight, onDeleteTransaction, onRetry,
}) {
  const noActivity = !loading && transactions.length === 0;

  // Delta strings (vs last month, expenses-side)
  const deltaCopy = useMemo(() => {
    if (!prevTotals) return null;
    const diffExpenses = totals.expenses - prevTotals.expenses;
    if (Math.abs(diffExpenses) < 1) return 'flat vs last month';
    return diffExpenses > 0
      ? `+${formatGBP(diffExpenses)} vs last month`
      : `${formatGBP(diffExpenses)} vs last month`;
  }, [prevTotals, totals]);

  return (
    <>
      {/* KPI row */}
      <div className="money-kpi-grid">
        {loading && transactions.length === 0 ? (
          <>
            <LoadingState rows={2} />
            <LoadingState rows={2} />
            <LoadingState rows={2} />
          </>
        ) : (
          <>
            <MetricCard
              label="Income"
              value={formatGBP(totals.income)}
              subtitle="this month"
              accent={colors.accentEnergy}
            />
            <MetricCard
              label="Expenses"
              value={formatGBP(totals.expenses)}
              subtitle={deltaCopy || 'this month'}
              accent={colors.accentSystem}
            />
            <MetricCard
              label="Net"
              value={(totals.net >= 0 ? '+' : '−') + '£' + Math.abs(totals.net).toFixed(2)}
              subtitle="income − expenses"
              accent={totals.net >= 0 ? colors.accentEnergy : 'var(--danger)'}
            />
          </>
        )}
        <style>{`
          .money-kpi-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 16px;
          }
          @media (max-width: 1024px) { .money-kpi-grid { grid-template-columns: repeat(2, 1fr); } }
          @media (max-width: 768px)  { .money-kpi-grid { grid-template-columns: 1fr; } }
        `}</style>
      </div>

      {/* Budget progress */}
      <Card padding={spacing.lg}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <SectionTitle>Budget Progress</SectionTitle>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: colors.textSecondary }}>
              Set budgets per category to track your spending against monthly targets.
            </p>
          </div>
          <button
            type="button"
            onClick={onBudgets}
            style={secondaryBtnStyle}
          >
            Set budgets →
          </button>
        </div>
        {loading && budgets.length === 0 ? (
          <LoadingState rows={3} />
        ) : budgets.length === 0 ? (
          <EmptyState
            title="No budgets yet"
            message="Pick the categories you want to keep an eye on each month."
            cta={{ label: 'Set your first budget', onClick: onBudgets }}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {budgetProgress.map((b) => (
              <BudgetBar key={b.category} item={b} />
            ))}
          </div>
        )}
      </Card>

      {/* Recent transactions */}
      <Card padding={spacing.lg}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <SectionTitle>Recent Transactions</SectionTitle>
          <button type="button" onClick={onLog} style={primaryBtnStyle}>+ Log Transaction</button>
        </div>
        {loading && transactions.length === 0 ? (
          <LoadingState rows={4} />
        ) : noActivity ? (
          <EmptyState
            title="Nothing logged yet"
            message="Tap +Log Transaction to add your first entry for this month."
            cta={{ label: '+ Log Transaction', onClick: onLog }}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {transactions.slice(0, 10).map((tx) => (
              <TransactionRow key={tx.id} tx={tx} onDelete={onDeleteTransaction} />
            ))}
          </div>
        )}
      </Card>

      {error && (
        <ErrorState
          title="Some data couldn't load"
          message={error}
          onRetry={onRetry}
          inline
        />
      )}

      {/* Action row */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          paddingTop: 4,
        }}
      >
        <button type="button" onClick={onBudgets} style={secondaryBtnStyle}>Budgets</button>
        <button type="button" onClick={onHistory} style={secondaryBtnStyle}>History</button>
        <button type="button" onClick={onInsight} style={secondaryBtnStyle}>Get Money Insight</button>
      </div>
    </>
  );
}

function BudgetBar({ item }) {
  const pct = Math.min(150, item.percent || 0); // cap visual at 150%
  const fillPct = Math.min(100, pct);
  const barColor =
    item.status === 'over' ? 'var(--danger)' :
    item.status === 'warning' ? 'var(--warning)' :
    'var(--success)';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>{item.category}</span>
        <span style={{ fontSize: 12, color: colors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
          {formatGBP(item.spent)} / {formatGBP(item.limit)}
          {item.status === 'over' && ` · over by ${formatGBP(item.over_amount)}`}
        </span>
      </div>
      <div style={{ position: 'relative', height: 6, borderRadius: 999, backgroundColor: 'var(--bg-hover)', overflow: 'hidden' }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            width: `${fillPct}%`,
            backgroundColor: barColor,
            borderRadius: 999,
            transition: 'width 240ms ease',
          }}
        />
      </div>
    </div>
  );
}

/* ================================================================
   Log Transaction view
   ================================================================ */

function LogTransactionView({ onSave, onCancel }) {
  const [logType, setLogType] = useState('expense');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('Food');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(todayLocalDateStr());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0 || isNaN(amt)) {
      setError('Enter a positive amount');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        log_type: logType,
        amount: amt,
        category,
        description: description.trim(),
        date,
      });
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card padding={spacing.lg}>
      <SectionTitle>Log a transaction</SectionTitle>
      <form onSubmit={submit} style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Type */}
        <div style={{ display: 'flex', gap: 8 }}>
          {['expense', 'income'].map((t) => {
            const active = logType === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setLogType(t)}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  minHeight: 44,
                  borderRadius: radius.sm,
                  border: `1.5px solid ${active ? colors.primary : colors.border}`,
                  background: active ? 'rgba(47, 91, 255, 0.06)' : '#fff',
                  color: active ? colors.primary : colors.textPrimary,
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: fontStack,
                  cursor: 'pointer',
                  textTransform: 'capitalize',
                }}
              >
                {t}
              </button>
            );
          })}
        </div>

        <Field label="Amount (£)">
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            style={inputStyle}
            required
          />
        </Field>

        <Field label="Category">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            style={inputStyle}
          >
            {DEFAULT_CATEGORIES.map((c) => (
              <option key={c.name} value={c.name}>{c.name}</option>
            ))}
          </select>
        </Field>

        <Field label="Description (optional)">
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 120))}
            placeholder="e.g., Tesco shop"
            maxLength={120}
            style={inputStyle}
          />
        </Field>

        <Field label="Date">
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={inputStyle}
          />
        </Field>

        {error && <ErrorState inline title="Couldn't save" message={error} />}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" onClick={onCancel} style={secondaryBtnStyle}>Cancel</button>
          <button type="submit" disabled={saving} style={{ ...primaryBtnStyle, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Card>
  );
}

/* ================================================================
   Budgets view
   ================================================================ */

function BudgetsView({ budgets, onSave, onClear }) {
  const byCat = budgets.reduce((acc, b) => { acc[b.category] = b; return acc; }, {});
  const [drafts, setDrafts] = useState({});
  const [savingCat, setSavingCat] = useState(null);

  const setDraft = (cat, val) => setDrafts((d) => ({ ...d, [cat]: val }));
  const handleSave = async (cat) => {
    const val = Number(drafts[cat]);
    if (!val || val <= 0 || isNaN(val)) return;
    setSavingCat(cat);
    try { await onSave({ category: cat, monthly_limit: val }); setDraft(cat, ''); } finally { setSavingCat(null); }
  };

  return (
    <Card padding={spacing.lg}>
      <SectionTitle>Budgets</SectionTitle>
      <p style={{ margin: '4px 0 16px', fontSize: 13, color: colors.textSecondary }}>
        Set a monthly cap per category. Curx will surface anything trending over.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {DEFAULT_CATEGORIES.map((c) => {
          const existing = byCat[c.name];
          const draft = drafts[c.name] ?? '';
          return (
            <div
              key={c.name}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto auto',
                gap: 10,
                alignItems: 'center',
                padding: '8px 4px',
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 500, color: colors.textPrimary }}>
                {c.name}
                {existing && (
                  <span style={{ marginLeft: 8, fontSize: 12, color: colors.textSecondary }}>
                    current: {formatGBP(existing.monthly_limit)}
                  </span>
                )}
              </div>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="1"
                value={draft}
                onChange={(e) => setDraft(c.name, e.target.value)}
                placeholder={existing ? 'change…' : 'set £/mo'}
                style={{ ...inputStyle, width: 140 }}
              />
              <button
                type="button"
                onClick={() => handleSave(c.name)}
                disabled={!draft || savingCat === c.name}
                style={{ ...primaryBtnStyle, padding: '8px 14px', minHeight: 36 }}
              >
                {savingCat === c.name ? 'Saving…' : existing ? 'Update' : 'Set'}
              </button>
              {existing && (
                <button
                  type="button"
                  onClick={() => onClear(c.name)}
                  style={{ ...secondaryBtnStyle, padding: '8px 12px', minHeight: 36, color: 'var(--danger)', borderColor: colors.border }}
                >
                  Clear
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ================================================================
   History view
   ================================================================ */

function HistoryView({ userId, onDelete }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      try {
        const since = new Date();
        since.setMonth(since.getMonth() - 3);
        const sinceStr = `${since.getFullYear()}-${String(since.getMonth() + 1).padStart(2, '0')}-01`;
        const { data, error } = await supabase
          .from('money_logs')
          .select('*')
          .eq('user_id', userId)
          .gte('date', sinceStr)
          .order('date', { ascending: false })
          .order('logged_at', { ascending: false });
        if (cancelled) return;
        if (error) throw error;
        setRows(data || []);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);

  return (
    <Card padding={spacing.lg}>
      <SectionTitle>History · last 3 months</SectionTitle>
      {loading ? (
        <LoadingState rows={5} />
      ) : error ? (
        <ErrorState title="Couldn't load history" message={error} inline />
      ) : rows.length === 0 ? (
        <EmptyState message="No transactions in the last three months." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 12 }}>
          {rows.map((tx) => (
            <TransactionRow
              key={tx.id}
              tx={{ ...tx, date: formatTransactionDate(tx.date) ? tx.date : tx.date }}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

/* ================================================================
   Insight view
   ================================================================ */

function InsightView({ loading, error, result, monthLabel, onRetry }) {
  return (
    <Card padding={spacing.lg} accentColor={colors.accentSystem}>
      <SectionTitle>Money Insight · {monthLabel}</SectionTitle>
      {loading ? (
        <div style={{ marginTop: 12 }}>
          <LoadingState rows={4} />
        </div>
      ) : error ? (
        <div style={{ marginTop: 12 }}>
          <ErrorState title="Couldn't generate insight" message={error} onRetry={onRetry} inline />
        </div>
      ) : !result ? (
        <EmptyState message="No insight yet — generate one above." />
      ) : (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {result.alert && (
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: colors.textPrimary,
                padding: 12,
                backgroundColor: 'var(--bg-hover)',
                borderRadius: radius.sm,
              }}
            >
              {result.alert}
            </div>
          )}
          <BulletList title="Observations" items={result.observations} />
          <BulletList title="Suggestions" items={result.suggestions} />
        </div>
      )}
    </Card>
  );
}

/* ================================================================
   Shared bits
   ================================================================ */

function SectionTitle({ children }) {
  return (
    <h3
      style={{
        margin: 0,
        fontSize: 14,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: colors.textSecondary,
        fontWeight: 600,
        fontFamily: fontStack,
      }}
    >
      {children}
    </h3>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: fontStack }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function BulletList({ title, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: colors.textSecondary,
          fontWeight: 600,
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((it, i) => (
          <li
            key={i}
            style={{
              fontSize: 14,
              color: colors.textPrimary,
              lineHeight: 1.5,
              paddingLeft: 12,
              borderLeft: `2px solid ${colors.border}`,
            }}
          >
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Toast({ message, type }) {
  const isError = type === 'error';
  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '10px 18px',
        borderRadius: 999,
        backgroundColor: isError ? 'var(--danger)' : colors.textPrimary,
        color: '#fff',
        fontSize: 13,
        fontWeight: 500,
        fontFamily: fontStack,
        boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
        zIndex: 100,
      }}
    >
      {message}
    </div>
  );
}

/* ---- shared button styles ---- */

const primaryBtnStyle = {
  padding: '10px 18px',
  minHeight: 40,
  borderRadius: 999,
  border: 'none',
  background: colors.primary,
  color: '#fff',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: 'pointer',
};

const secondaryBtnStyle = {
  padding: '10px 16px',
  minHeight: 40,
  borderRadius: 999,
  background: 'transparent',
  border: `1px solid ${colors.primary}`,
  color: colors.primary,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: fontStack,
  cursor: 'pointer',
};

const inputStyle = {
  padding: '10px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: 14,
  fontFamily: fontStack,
  color: colors.textPrimary,
  background: '#fff',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};
